import { apiFetch, apiUrl, OFFLINE_MESSAGE } from "./api";

/**
 * Client for QR attendance (`/api/attendance/*` for HR, `/api/portal/attendance/*`
 * for the self-service portal). Web-only — the admin app has no attendance pages.
 */

export class AttendanceApiError extends Error {
  status: number;
  code: string | null;
  details: Record<string, unknown>;

  constructor(
    message: string,
    status: number,
    code: string | null,
    details: Record<string, unknown>,
  ) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await apiFetch(apiUrl(path), {
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
      ...init,
    });
  } catch (err) {
    if (err instanceof TypeError && /fetch/i.test(err.message)) {
      throw new AttendanceApiError(OFFLINE_MESSAGE, 0, null, {});
    }
    throw err;
  }
  const data = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (!res.ok) {
    const message =
      (data && "error" in data ? String(data.error) : null) ?? `Request failed (${res.status})`;
    const code = data && typeof data.code === "string" ? data.code : null;
    throw new AttendanceApiError(message, res.status, code, data ?? {});
  }
  return data as T;
}

/** The browser's IANA zone, so exported times match the clock on the wall. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/* ── Types ───────────────────────────────────────────────────────────────── */

export type SessionStatus = "scheduled" | "open" | "closed" | "cancelled";
export type SessionScope = "upcoming" | "past" | "all";

export type AttendanceSession = {
  id: string;
  title: string;
  description: string | null;
  location: string | null;
  facilitator: string | null;
  startsAt: string;
  endsAt: string;
  status: SessionStatus;
  /** Whether HR may (re)open check-in — false once cancelled or past closing. */
  canOpen: boolean;
  /** HR may add people by hand and correct those manual records. Records from
   *  a QR scan can't be changed either way. */
  allowManualCheckIn: boolean;
  /** The default when HR closes check-in early: check out anyone still in.
   *  Closing by itself at the end time always checks everyone out. */
  autoCheckOut: boolean;
  createdAt: string;
  updatedAt: string;
};

export type AttendanceSessionListItem = AttendanceSession & {
  checkedIn: number;
  checkedOut: number;
};

export type AttendanceSessionPage = {
  items: AttendanceSessionListItem[];
  total: number;
  page: number;
  pageSize: number;
};

export type AttendanceRecord = {
  id: string;
  sessionId: string;
  employeeId: string;
  name?: string;
  jobTitle: string | null;
  department: string | null;
  checkedInAt: string;
  checkedOutAt: string | null;
  minutes: number | null;
  method: "qr" | "manual";
  note: string | null;
  /** The check-out was filled in because check-in closed — by HR or by itself. */
  autoCheckedOut: boolean;
  recordedByName: string | null;
};

export type AttendanceSessionDetail = {
  session: AttendanceSession & { checkedIn: number; checkedOut: number };
  records: AttendanceRecord[];
};

export type SessionInput = {
  title: string;
  description?: string | null;
  location?: string | null;
  facilitator?: string | null;
  startsAt: string;
  endsAt: string;
  /** Left out on create → the workspace default. */
  allowManualCheckIn?: boolean;
  /** Left out on create → the workspace default. */
  autoCheckOut?: boolean;
};

/** Sessions created this monthly window against the account's limit. */
export type AttendanceUsage = {
  used: number;
  /** -1 = unlimited; 0 = no new sessions. */
  limit: number;
  /** A platform admin set this account's own limit. */
  custom: boolean;
  windowStart: string;
  resetsAt: string;
};

export type DisplayState = {
  session: AttendanceSession;
  status: SessionStatus;
  token: string | null;
  issuedAt: string | null;
  expiresAt: string | null;
  serverTime: string;
  checkedIn: number;
  checkedOut: number;
  recent: { name: string; action: "in" | "out"; at: string }[];
};

export type HistoryRecord = AttendanceRecord & {
  sessionTitle: string;
  sessionStartsAt: string;
  sessionEndsAt: string;
  sessionLocation: string | null;
};

export type EmployeeHistory = {
  employee: { id: string; name: string; jobTitle: string | null; department: string | null };
  records: HistoryRecord[];
};

export type SummaryReport = {
  from: string;
  to: string;
  labels: {
    people: { singular: string; plural: string };
    session: { singular: string; plural: string };
  };
  sessions: {
    id: string;
    title: string;
    startsAt: string;
    endsAt: string;
    location: string | null;
    facilitator: string | null;
    status: string;
    checkedIn: number;
    checkedOut: number;
    avgMinutes: number | null;
  }[];
  people: {
    employeeId: string;
    name: string;
    jobTitle: string | null;
    department: string | null;
    sessionsAttended: number;
    missingCheckouts: number;
    totalMinutes: number;
  }[];
};

/* ── HR ──────────────────────────────────────────────────────────────────── */

export function fetchSessionsPage(params: {
  scope: SessionScope;
  q?: string;
  page: number;
  sort?: "title" | "date" | "location" | "attendance";
  dir?: "asc" | "desc";
}): Promise<AttendanceSessionPage> {
  const qs = new URLSearchParams({ scope: params.scope, page: String(params.page) });
  if (params.q) qs.set("q", params.q);
  if (params.sort) qs.set("sort", params.sort);
  if (params.dir) qs.set("dir", params.dir);
  return request(`/api/attendance/sessions?${qs}`);
}

export function fetchSession(id: string): Promise<AttendanceSessionDetail> {
  return request(`/api/attendance/sessions/${encodeURIComponent(id)}`);
}

export function createSession(input: SessionInput): Promise<AttendanceSession> {
  return request("/api/attendance/sessions", { method: "POST", body: JSON.stringify(input) });
}

export function updateSession(id: string, input: SessionInput): Promise<AttendanceSession> {
  return request(`/api/attendance/sessions/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export function deleteSession(id: string): Promise<{ ok: true }> {
  return request(`/api/attendance/sessions/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export function setSessionState(id: string, action: "open" | "cancel"): Promise<AttendanceSession> {
  return request(`/api/attendance/sessions/${encodeURIComponent(id)}/${action}`, {
    method: "POST",
  });
}

export type CloseSummary = {
  /** Already checked out before the close. */
  checkedOut: number;
  /** Checked out by the close. */
  autoCheckedOut: number;
  /** Left without a check-out. */
  leftOpen: number;
};

/** Closes check-in. `checkOutOpen` checks out everyone still checked in at the
 *  session's end time (or now, when closing early). */
export function closeSession(
  id: string,
  checkOutOpen: boolean,
): Promise<AttendanceSession & { closeSummary: CloseSummary }> {
  return request(`/api/attendance/sessions/${encodeURIComponent(id)}/close`, {
    method: "POST",
    body: JSON.stringify({ checkOutOpen }),
  });
}

export function fetchDisplayState(id: string): Promise<DisplayState> {
  return request(`/api/attendance/sessions/${encodeURIComponent(id)}/display`, {
    cache: "no-store",
  });
}

export function addManualRecord(
  sessionId: string,
  input: {
    employeeId: string;
    checkedInAt: string;
    checkedOutAt?: string | null;
    note?: string | null;
  },
): Promise<AttendanceRecord> {
  return request(`/api/attendance/sessions/${encodeURIComponent(sessionId)}/records`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateRecord(
  sessionId: string,
  recordId: string,
  input: { checkedInAt?: string; checkedOutAt?: string | null; note?: string | null },
): Promise<AttendanceRecord> {
  return request(
    `/api/attendance/sessions/${encodeURIComponent(sessionId)}/records/${encodeURIComponent(recordId)}`,
    { method: "PATCH", body: JSON.stringify(input) },
  );
}

export function deleteRecord(sessionId: string, recordId: string): Promise<{ ok: true }> {
  return request(
    `/api/attendance/sessions/${encodeURIComponent(sessionId)}/records/${encodeURIComponent(recordId)}`,
    { method: "DELETE" },
  );
}

export function fetchAttendanceUsage(): Promise<AttendanceUsage> {
  return request("/api/attendance/usage");
}

export function fetchEmployeeHistory(employeeId: string): Promise<EmployeeHistory> {
  return request(`/api/attendance/people/${encodeURIComponent(employeeId)}/history`);
}

export function fetchSummaryReport(from: string, to: string): Promise<SummaryReport> {
  const qs = new URLSearchParams({ from, to, tz: browserTimeZone() });
  return request(`/api/attendance/reports/summary?${qs}`);
}

/** Saves a blob through a temporary link — used by the PDF exports. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/* ── Portal ──────────────────────────────────────────────────────────────── */

export type PortalAttendanceRecord = {
  id: string;
  sessionId: string;
  sessionTitle: string;
  sessionStartsAt: string;
  sessionEndsAt: string;
  sessionLocation: string | null;
  /** Ended sessions are already stored closed — the server settles them first. */
  sessionStatus: SessionStatus;
  checkedInAt: string;
  checkedOutAt: string | null;
  minutes: number | null;
  autoCheckedOut: boolean;
};

export type ScanSuccess = {
  outcome: "checked_in" | "checked_out";
  session: AttendanceSession;
  checkedInAt: string;
  checkedOutAt: string | null;
  minutes: number | null;
};

export type ScanRejectionCode =
  | "session_not_found"
  | "session_closed"
  | "already_checked_in"
  | "already_complete"
  | "code_expired"
  | "checkout_expired";

export function scanAttendance(sessionId: string, token: string): Promise<ScanSuccess> {
  return request("/api/portal/attendance/scan", {
    method: "POST",
    body: JSON.stringify({ sessionId, token }),
  });
}

/** Checks out with the ticket an `already_checked_in` scan handed back, once
 *  the person has confirmed. The ticket is single-use and lasts five minutes. */
export function confirmAttendanceCheckout(ticket: string): Promise<ScanSuccess> {
  return request("/api/portal/attendance/checkout", {
    method: "POST",
    body: JSON.stringify({ ticket }),
  });
}

/**
 * The session id and token from a scanned QR code. The code is a URL —
 * `…/portal/attendance/scan?s=<session>&t=<token>` — so a phone's own camera
 * app lands on the right page too; this also accepts the bare query string.
 */
export function parseAttendanceCode(raw: string): { sessionId: string; token: string } | null {
  const text = raw.trim();
  let params: URLSearchParams;
  try {
    params = new URL(text).searchParams;
  } catch {
    params = new URLSearchParams(text.startsWith("?") ? text.slice(1) : text);
  }
  const sessionId = params.get("s");
  const token = params.get("t");
  if (!sessionId || !token) return null;
  return { sessionId, token };
}

/** The value the display encodes into its QR code. */
export function attendanceCodeUrl(origin: string, sessionId: string, token: string): string {
  const qs = new URLSearchParams({ s: sessionId, t: token });
  return `${origin}/portal/attendance/scan?${qs}`;
}

/* ── Formatting ──────────────────────────────────────────────────────────── */

/** `3h 43m`; "—" when there's no check-out to measure against. */
export function formatMinutes(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) return "—";
  const total = Math.max(0, Math.round(minutes));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
}
